import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

type Format = 'cash' | 'sitgo' | 'tournament';
type View = { format: Format; positions: Record<Format, number>; day: string };
const emptyView = (): View => ({
  format: 'cash',
  positions: { cash: 0, sitgo: 0, tournament: 0 },
  day: ''
});
// Session-only navigation state. A reload starts fresh; changing accounts discards it.
let saved: { scope: string; view: View } | undefined;

export function useLobbyView(scope: string) {
  const view = useMemo(() => {
    if (!scope) {
      saved = undefined;
      return emptyView();
    }
    if (saved?.scope !== scope) saved = { scope, view: emptyView() };
    return saved.view;
  }, [scope]);
  const [selection, setSelection] = useState({ view, format: view.format });
  const format = selection.view === view ? selection.format : view.format;
  const scrollRef = useRef<HTMLDivElement>(null);
  const pendingRestore = useRef(false);
  const tournamentDayRef = useMemo(() => ({ current: view.day }), [view]);
  const setFormat = useCallback(
    (next: Format) => {
      if (scrollRef.current && !pendingRestore.current) {
        view.positions[format] = Math.max(0, scrollRef.current.scrollTop);
      }
      // Save before React swaps the list and the browser clamps its old offset.
      view.format = next;
      setSelection({ view, format: next });
    },
    [view, format]
  );

  useLayoutEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    view.format = format;
    const target = view.positions[format];
    let restoring = true;
    pendingRestore.current = true;
    const restore = () => {
      if (!restoring) return;
      root.scrollTop = target;
      if (root.scrollHeight - root.clientHeight >= target) {
        restoring = false;
        pendingRestore.current = false;
      }
    };
    const remember = () => {
      if (!restoring) view.positions[format] = Math.max(0, root.scrollTop);
    };
    // Explicit input wins over a pending restoration on a slow-loading list.
    const takeControl = () => {
      restoring = false;
      pendingRestore.current = false;
      remember();
    };
    const keyControl = (event: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key))
        takeControl();
    };
    const resize = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(restore);
    const observeChildren = () => {
      resize?.disconnect();
      resize?.observe(root);
      Array.from(root.children).forEach((child) => resize?.observe(child));
      restore();
    };
    const mutations = new MutationObserver(observeChildren);
    mutations.observe(root, { childList: true, subtree: true });
    observeChildren();
    root.addEventListener('scroll', remember, { passive: true });
    root.addEventListener('wheel', takeControl, { passive: true });
    root.addEventListener('touchstart', takeControl, { passive: true });
    root.addEventListener('pointerdown', takeControl, { passive: true });
    root.addEventListener('keydown', keyControl);
    return () => {
      if (view.format === format) remember();
      view.day = tournamentDayRef.current;
      resize?.disconnect();
      mutations.disconnect();
      root.removeEventListener('scroll', remember);
      root.removeEventListener('wheel', takeControl);
      root.removeEventListener('touchstart', takeControl);
      root.removeEventListener('pointerdown', takeControl);
      root.removeEventListener('keydown', keyControl);
    };
  }, [format, view, tournamentDayRef]);

  return { format, setFormat, scrollRef, tournamentDayRef };
}
